import type { FC } from 'react';
import { ModalWrapper } from 'src/components/form/form/ModalWrapper';
import type { SaveActionResult } from 'src/components/form/form/types';
import { useGetIndicatorById } from 'src/hooks/queries';
import { buildCopiedIndicatorDefaults } from 'src/pages/indicators/forms/buildCopiedIndicatorDefaults';
import type { IndicatorFormDataFields } from 'src/pages/indicators/forms/indicatorSchema';

import IndicatorsDetailsForm from '../forms/IndicatorDetailsForm';

type Props = {
  onDismiss: () => void;
  onSave: (
    control: IndicatorFormDataFields
  ) => Promise<SaveActionResult | void>;
  // When set, the modal opens pre-populated from this source Indicator; absent
  // for a normal blank create.
  sourceIndicatorId?: string;
};

const CreateIndicatorModal: FC<Props> = ({
  onDismiss,
  onSave,
  sourceIndicatorId,
}) => {
  const isCopyMode = Boolean(sourceIndicatorId);
  const { data, loading, error } = useGetIndicatorById({
    queryArgs: { id: sourceIndicatorId ?? '' },
    shouldSkip: !isCopyMode,
  });

  // Surface a source-fetch failure rather than silently opening a blank modal
  // (e.g. the source was deleted between selection and open).
  if (isCopyMode && error) {
    throw error;
  }

  // Render nothing until the source resolves so the form mounts once with the
  // copied defaults (react-hook-form applies defaultValues on mount only).
  if (isCopyMode && loading) {
    return null;
  }

  const source = data?.indicator?.[0];

  // Copy mode requires a resolved source. A successful-but-empty result (e.g.
  // the source was deleted between selection and open) must not silently fall
  // back to a blank create — surface it instead.
  if (isCopyMode && !source) {
    throw new Error(`Source Indicator ${sourceIndicatorId} not found for copy`);
  }

  const copiedDefaults = source
    ? buildCopiedIndicatorDefaults(source)
    : undefined;

  return (
    <IndicatorsDetailsForm
      onSave={onSave}
      onDismiss={onDismiss}
      defaultValues={copiedDefaults}
      renderTemplate={(renderProps) => (
        <ModalWrapper
          {...renderProps}
          visible={true}
          testId={'indicator-modal'}
        />
      )}
    />
  );
};

export default CreateIndicatorModal;
